import { WireAnalyticsEvent } from 'scenes/common/types';
import {
  MotorFunnelState,
  ReferenceData,
  VehicleFromSearch,
  VehicleManuallyEntered,
  WireMotorAnalytics,
  VehicleSearchType,
  MotorSection,
  MotorSubSection,
} from 'scenes/motor/types';
import {
  getVehicleFromSearch,
  getVehicleManuallyEntered,
  getVehicleSearchType,
  isVehicleManualEntry,
} from 'scenes/motor/selectors';

import { toWirePage } from './page';
import { toWireFormData } from './formData';
import { toWireCar } from './car';
import { toWireDriver } from './driver';
import { toWireEdit } from './edit';
import { toWireError } from './error';

export const marshalViewLoadedEvent = (
  state: MotorFunnelState,
  referenceData: ReferenceData
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.ViewLoaded,
  page: toWirePage(state),
  formData: toWireFormData(state),
  car: toWireCar(
    state,
    getVehicleSearchType(state),
    isVehicleManualEntry(state)
      ? getVehicleManuallyEntered(state)
      : getVehicleFromSearch(state),
    referenceData
  ),
  driver: toWireDriver(state),
});

export const marshalSaveQuoteEvent = (
  state: MotorFunnelState
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.SaveQuote,
  page: toWirePage(state),
  formData: toWireFormData(state),
});

export const marshalEmailQuoteEvent = (
  state: MotorFunnelState
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.EmailQuote,
  page: toWirePage(state),
  formData: toWireFormData(state),
});

export const marshalResumeQuoteEvent = (
  state: MotorFunnelState
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.ResumeQuote,
  page: toWirePage(state),
  formData: toWireFormData(state),
});

export const marshalRetrieveQuoteStartEvent = (
  state: MotorFunnelState
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.RetrieveQuoteStart,
  page: toWirePage(state),
  formData: toWireFormData(state),
});

export const marshalRetrieveQuoteCompleteEvent = (
  state: MotorFunnelState
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.RetrieveQuoteComplete,
  page: toWirePage(state),
  formData: toWireFormData(state),
});

export const marshalFindCarEvent = (
  state: MotorFunnelState,
  vehicle: VehicleFromSearch,
  referenceData: ReferenceData
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.FindCar,
  page: toWirePage(state),
  formData: toWireFormData(state),
  car: toWireCar(state, VehicleSearchType.RegoLookup, vehicle, referenceData),
});

export const marshalCarNotFoundEvent = (
  state: MotorFunnelState
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.CarNotFound,
  page: toWirePage(state),
  formData: toWireFormData(state),
});

export const marshalManualSearchEvent = (
  state: MotorFunnelState,
  vehicle: VehicleManuallyEntered,
  referenceData: ReferenceData
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.ManualSearch,
  page: toWirePage(state),
  formData: toWireFormData(state),
  car: toWireCar(state, VehicleSearchType.ManualEntry, vehicle, referenceData),
});

export const marshalCarNotAvailableEvent = (
  state: MotorFunnelState
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.CarNotAvailable,
  page: toWirePage(state),
  formData: toWireFormData(state),
});

export const marshalAddDriverEvent = (
  state: MotorFunnelState,
  referenceData: ReferenceData
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.AddDriver,
  page: toWirePage(state),
  formData: toWireFormData(state),
  car: toWireCar(
    state,
    getVehicleSearchType(state),
    isVehicleManualEntry(state)
      ? getVehicleManuallyEntered(state)
      : getVehicleFromSearch(state),
    referenceData
  ),
  driver: toWireDriver(state),
});

export const marshalDriverAddedEvent = (
  state: MotorFunnelState,
  referenceData: ReferenceData
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.DriverAdded,
  page: toWirePage(state),
  formData: toWireFormData(state),
  car: toWireCar(
    state,
    getVehicleSearchType(state),
    isVehicleManualEntry(state)
      ? getVehicleManuallyEntered(state)
      : getVehicleFromSearch(state),
    referenceData
  ),
  driver: toWireDriver(state),
});

export const marshalOptionalExtrasEvent = (
  state: MotorFunnelState,
  referenceData: ReferenceData
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.OptionalExtras,
  page: toWirePage(state),
  formData: toWireFormData(state),
  car: toWireCar(
    state,
    getVehicleSearchType(state),
    isVehicleManualEntry(state)
      ? getVehicleManuallyEntered(state)
      : getVehicleFromSearch(state),
    referenceData
  ),
  driver: toWireDriver(state),
});

export const marshalAddPolicyEvent = (
  state: MotorFunnelState,
  referenceData: ReferenceData
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.AddPolicy,
  page: toWirePage(state),
  formData: toWireFormData(state),
  car: toWireCar(
    state,
    getVehicleSearchType(state),
    isVehicleManualEntry(state)
      ? getVehicleManuallyEntered(state)
      : getVehicleFromSearch(state),
    referenceData
  ),
  driver: toWireDriver(state),
});

export const marshalEditEvent = (
  state: MotorFunnelState,
  referenceData: ReferenceData,
  motorSection?: MotorSection,
  motorSubSection?: MotorSubSection
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.Edit,
  page: toWirePage(state),
  formData: toWireFormData(state),
  car: toWireCar(
    state,
    getVehicleSearchType(state),
    isVehicleManualEntry(state)
      ? getVehicleManuallyEntered(state)
      : getVehicleFromSearch(state),
    referenceData
  ),
  driver: toWireDriver(state),
  edit: toWireEdit(state, motorSection, motorSubSection),
});

export const marshalQuoteReferred = (
  state: MotorFunnelState,
  quoteNumber: string,
  referenceData: ReferenceData
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.QuoteReferred,
  page: toWirePage(state),
  formData: toWireFormData(state, quoteNumber),
  car: toWireCar(
    state,
    getVehicleSearchType(state),
    isVehicleManualEntry(state)
      ? getVehicleManuallyEntered(state)
      : getVehicleFromSearch(state),
    referenceData
  ),
  driver: toWireDriver(state),
});

export const marshalQuoteDeclined = (
  state: MotorFunnelState,
  quoteNumber: string,
  referenceData: ReferenceData
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.QuoteDeclined,
  page: toWirePage(state),
  formData: toWireFormData(state, quoteNumber),
  car: toWireCar(
    state,
    getVehicleSearchType(state),
    isVehicleManualEntry(state)
      ? getVehicleManuallyEntered(state)
      : getVehicleFromSearch(state),
    referenceData
  ),
  driver: toWireDriver(state),
});

export const marshalFindOutMoreEvent = (
  state: MotorFunnelState
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.FindOutMore,
  page: toWirePage(state),
  formData: toWireFormData(state),
});

export const marshalCallUsEvent = (
  state: MotorFunnelState
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.CallUs,
  page: toWirePage(state),
  formData: toWireFormData(state),
});

export const marshalErrorEvent = (
  state: MotorFunnelState,
  message: string,
  type: string,
  quoteNumber?: string
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.Error,
  page: toWirePage(state),
  formData: toWireFormData(state, quoteNumber),
  error: toWireError(state, type, message),
});

export const marshalSubmitQuoteEvent = (
  state: MotorFunnelState,
  referenceData: ReferenceData
): WireMotorAnalytics => ({
  event: WireAnalyticsEvent.SubmitQuote,
  page: toWirePage(state),
  formData: toWireFormData(state),
  car: toWireCar(
    state,
    getVehicleSearchType(state),
    isVehicleManualEntry(state)
      ? getVehicleManuallyEntered(state)
      : getVehicleFromSearch(state),
    referenceData
  ),
  driver: toWireDriver(state),
});
